import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faArrowLeft,
  faChartColumn,
  faChartPie,
  faChevronDown,
  faChevronRight,
  faInbox,
  faUpRightFromSquare,
  faSortDown,
  faSortUp,
} from '@fortawesome/free-solid-svg-icons';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Survey, SurveyQuestionType, SurveyResponse } from '../../types';
import { API_BASE_URL } from '../../config/api';
import SurveyResultsFilters, {
  applySurveyResultsFilters,
  emptySurveyResultsFilters,
  surveyResultsFiltersFromSearch,
  surveyResultsFiltersToSearch,
} from './SurveyResultsFilters';
import SurveyUserHover from './SurveyUserHover';
import { surveyAnswerColor } from './surveyUtils';

const PAGE_SIZE = 25;

interface QuestionAnswerBucket {
  value: string;
  label: string;
  count: number;
}

interface QuestionSummary {
  questionId: string;
  prompt: string;
  type: SurveyQuestionType;
  answeredCount: number;
  uniqueAnswerCount?: number;
  truncated?: boolean;
  answers: QuestionAnswerBucket[];
}

interface ResultsSummary {
  completedCount: number;
  optedOutCount: number;
  totalCount: number;
  questions: QuestionSummary[];
}

const formatDate = (value?: string | Date | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatAnswer = (value: string, type?: SurveyQuestionType) => {
  if (!value) return '—';
  if (type === 'boolean') {
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
  }
  return value;
};

const formatPhonePlatform = (platform?: string) => {
  if (platform === 'ios') return 'iOS';
  if (platform === 'android') return 'Android';
  return '—';
};

const truncate = (value: string, max = 28) => {
  const text = String(value || '').trim();
  if (!text) return '—';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const ChartTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: QuestionAnswerBucket }>;
}) => {
  if (!active || !payload?.length) return null;
  const bucket = payload[0]?.payload;
  if (!bucket) return null;
  return (
    <div className="survey-results-chart-tooltip">
      <strong>{bucket.label}</strong>
      <span>{bucket.count.toLocaleString()} response{bucket.count === 1 ? '' : 's'}</span>
    </div>
  );
};

const piePercentLabel = ({
  percent,
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
}: {
  percent?: number;
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
}) => {
  if (!percent || percent < 0.08 || cx == null || cy == null || midAngle == null || innerRadius == null || outerRadius == null) {
    return null;
  }
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
  const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${Math.round(percent * 100)}%`}
    </text>
  );
};

const QuestionResultChart: React.FC<{
  question: QuestionSummary;
  surveyId?: string;
  search: string;
}> = ({ question, surveyId, search }) => {
  const [chartType, setChartType] = useState<'bar' | 'pie'>('bar');
  const barHeight = Math.max(140, question.answers.length * 36 + 24);
  const empty = question.answers.every((answer) => answer.count === 0);

  return (
    <section className="card shadow-sm border-0 survey-results-chart-card">
      <div className="card-body">
        <div className="survey-results-chart-head">
          <h3>{question.prompt}</h3>
          <div className="survey-results-chart-meta">
            <span>
              {question.answeredCount.toLocaleString()} answer{question.answeredCount === 1 ? '' : 's'}
            </span>
            {!empty ? (
              <div className="btn-group btn-group-sm" role="group" aria-label="Chart type">
                <button
                  type="button"
                  className={`btn ${chartType === 'bar' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setChartType('bar')}
                  title="Bar chart"
                >
                  <FontAwesomeIcon icon={faChartColumn} />
                </button>
                <button
                  type="button"
                  className={`btn ${chartType === 'pie' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setChartType('pie')}
                  title="Pie chart"
                >
                  <FontAwesomeIcon icon={faChartPie} />
                </button>
              </div>
            ) : null}
          </div>
        </div>
        {empty ? (
          <div className="text-muted">No answers yet</div>
        ) : chartType === 'pie' ? (
          <div className="survey-results-pie">
            <div className="survey-results-pie-chart">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={question.answers}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius="78%"
                    labelLine={false}
                    label={piePercentLabel}
                  >
                    {question.answers.map((answer, index) => (
                      <Cell key={`${question.questionId}-${answer.value || answer.label}`} fill={surveyAnswerColor(answer.label, index)} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="survey-results-pie-legend">
              {question.answers.map((answer, index) => (
                <div key={`${question.questionId}-legend-${answer.value || answer.label}`} className="survey-results-pie-legend-item">
                  <i style={{ background: surveyAnswerColor(answer.label, index) }} />
                  <span title={answer.label}>{truncate(answer.label, 28)}</span>
                  <em>{answer.count.toLocaleString()}</em>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ width: '100%', height: barHeight }}>
            <ResponsiveContainer>
              <BarChart
                data={question.answers}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={132}
                  tick={{ fontSize: 12, fill: '#374151' }}
                  tickFormatter={(value: string) => truncate(value, 22)}
                />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" name="Responses" radius={[0, 4, 4, 0]}>
                  {question.answers.map((answer, index) => (
                    <Cell key={`${question.questionId}-${answer.value || answer.label}`} fill={surveyAnswerColor(answer.label, index)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {question.type === 'text' ? (
          <div className="survey-results-see-all">
            {question.truncated && question.uniqueAnswerCount ? (
              <div className="text-muted small mb-2">
                Showing 5 of {question.uniqueAnswerCount.toLocaleString()} unique answers
              </div>
            ) : null}
            <Link
              className="btn btn-outline-secondary btn-sm"
              to={`/surveys/${surveyId}/results/questions/${question.questionId}${search ? `?${search}` : ''}`}
            >
              See All Responses
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
};

const SurveyResultsPage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [summary, setSummary] = useState<ResultsSummary | null>(null);
  const [results, setResults] = useState<SurveyResponse[]>([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [sortColumn, setSortColumn] = useState('completedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [filters, setFilters] = useState(surveyResultsFiltersFromSearch(searchParams));

  const loadHeader = useCallback(async () => {
    if (!surveyId) return;
    setLoadingPage(true);
    try {
      const [surveyResponse, summaryResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/surveys/${surveyId}`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/surveys/${surveyId}/results/summary`, { withCredentials: true }),
      ]);
      setSurvey(surveyResponse.data.survey);
      setSummary(summaryResponse.data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading survey results:', err);
      setError(err.response?.data?.message || 'Failed to load survey results');
    } finally {
      setLoadingPage(false);
    }
  }, [surveyId]);

  const loadResults = useCallback(async (page: number) => {
    if (!surveyId) return;
    setLoadingTable(true);
    try {
      const params: Record<string, string | number> = {
        page,
        limit: PAGE_SIZE,
        sortColumn,
        sortDirection,
      };
      applySurveyResultsFilters(params, searchParams);

      const response = await axios.get(`${API_BASE_URL}/surveys/${surveyId}/results`, {
        withCredentials: true,
        params,
      });
      setResults(response.data.results || []);
      setTotalResults(response.data.totalResults || 0);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(response.data.currentPage || page);
      setExpandedId(null);
      setError(null);
    } catch (err: any) {
      console.error('Error loading survey responses:', err);
      setError(err.response?.data?.message || 'Failed to load survey responses');
    } finally {
      setLoadingTable(false);
    }
  }, [searchParams, sortColumn, sortDirection, surveyId]);

  useEffect(() => {
    loadHeader();
  }, [loadHeader]);

  useEffect(() => {
    setFilters(surveyResultsFiltersFromSearch(searchParams));
    loadResults(1);
  }, [loadResults, searchParams]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchParams(surveyResultsFiltersToSearch(filters));
  };

  const handleClearSearch = () => {
    setFilters(emptySurveyResultsFilters());
    setSearchParams({});
  };

  const handleSortChange = (column: string) => {
    if (column === sortColumn) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'completedAt' ? 'desc' : 'asc');
  };

  const renderSortIndicator = (column: string) => {
    const upColor = column === sortColumn && sortDirection === 'asc' ? '#333333' : '#dadee3';
    const downColor = column === sortColumn && sortDirection === 'desc' ? '#333333' : '#dadee3';
    return (
      <span className="ms-1" style={{ display: 'inline-block', position: 'relative', width: '10px', height: '14px' }}>
        <FontAwesomeIcon icon={faSortUp} style={{ color: upColor, position: 'absolute', top: '-1px', left: 0 }} />
        <FontAwesomeIcon icon={faSortDown} style={{ color: downColor, position: 'absolute', bottom: '-2px', left: 0 }} />
      </span>
    );
  };

  const completedCount = summary?.completedCount || 0;
  const optedOutCount = summary?.optedOutCount || 0;
  const hasFilters = Array.from(searchParams.keys()).length > 0;

  return (
    <div className="container-fluid mt-3 mb-5 survey-results-page">
      <div className="mb-3">
        <Link to={surveyId ? `/surveys/${surveyId}` : '/surveys'} className="btn btn-sm btn-outline-secondary">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Link>
      </div>

      {loadingPage ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '180px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error && !summary ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <h2 className="d-flex align-items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faInbox} className="text-primary survey-page-title-icon" />
            <span>
              Results
              {survey?.name ? <span className="fw-normal"> - {survey.name}</span> : null}
            </span>
          </h2>
          <div className="text-muted mb-4">
            {completedCount.toLocaleString()} result{completedCount === 1 ? '' : 's'}
            {optedOutCount > 0 ? ` · ${optedOutCount.toLocaleString()} opted out` : ''}
          </div>

          {summary?.questions?.length ? (
            <div className="survey-results-charts">
              {summary.questions.map((question) => (
                <QuestionResultChart
                  key={question.questionId}
                  question={question}
                  surveyId={surveyId}
                  search={searchParams.toString()}
                />
              ))}
            </div>
          ) : (
            <div className="deploy-test-card-empty mb-4">No questions on this survey</div>
          )}

          <section className="card shadow-sm border-0 survey-reports-section">
            <div className="card-body">
              <h5 className="card-title mb-3">Survey Reports</h5>
              <SurveyResultsFilters
                email={filters.email}
                firstName={filters.firstName}
                lastName={filters.lastName}
                serial={filters.serial}
                platform={filters.platform}
                status={filters.status}
                dateStart={filters.dateStart}
                dateEnd={filters.dateEnd}
                onEmail={(email) => setFilters((current) => ({ ...current, email }))}
                onFirstName={(firstName) => setFilters((current) => ({ ...current, firstName }))}
                onLastName={(lastName) => setFilters((current) => ({ ...current, lastName }))}
                onSerial={(serial) => setFilters((current) => ({ ...current, serial }))}
                onPlatform={(platform) => setFilters((current) => ({ ...current, platform }))}
                onStatus={(status) => setFilters((current) => ({ ...current, status }))}
                onDateStart={(dateStart) => setFilters((current) => ({ ...current, dateStart }))}
                onDateEnd={(dateEnd) => setFilters((current) => ({ ...current, dateEnd }))}
                onSubmit={handleSearch}
                onClear={handleClearSearch}
              />

          {error ? <div className="alert alert-danger">{error}</div> : null}

          {loadingTable ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '220px' }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <table className="table table-striped survey-results-table">
                <thead>
                  <tr>
                    <th style={{ width: '28px' }} />
                    <th onClick={() => handleSortChange('user')} style={{ cursor: 'pointer' }}>
                      User {renderSortIndicator('user')}
                    </th>
                    <th onClick={() => handleSortChange('status')} style={{ cursor: 'pointer' }}>
                      Status {renderSortIndicator('status')}
                    </th>
                    <th onClick={() => handleSortChange('completedAt')} style={{ cursor: 'pointer' }}>
                      Completed {renderSortIndicator('completedAt')}
                    </th>
                    <th>Phone</th>
                    <th>Devices</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.length > 0 ? (
                    results.map((result) => {
                      const expanded = expandedId === result._id;
                      const devices = (result.deviceSerials || []).join(', ');
                      return (
                        <React.Fragment key={result._id}>
                          <tr
                            className="survey-results-row"
                            onClick={() => setExpandedId(expanded ? null : result._id)}
                          >
                            <td>
                              <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} className="text-muted" />
                            </td>
                            <td>
                              <SurveyUserHover
                                result={result}
                                onClick={(event) => event.stopPropagation()}
                              />
                            </td>
                            <td>
                              <span className={`survey-results-status is-${result.status === 'opted_out' ? 'opted-out' : 'completed'}`}>
                                {result.status === 'opted_out' ? 'Opted out' : 'Completed'}
                              </span>
                            </td>
                            <td className="text-nowrap">{formatDate(result.completedAt)}</td>
                            <td>{formatPhonePlatform(result.phonePlatform)}</td>
                            <td title={devices}>{devices ? truncate(devices, 24) : '—'}</td>
                            <td className="text-nowrap">
                              <Link
                                className="btn btn-outline-primary btn-sm"
                                to={`/surveys/${surveyId}/results/${result._id}`}
                                title="Full report"
                                onClick={(event) => event.stopPropagation()}
                              >
                                Full Report <FontAwesomeIcon icon={faUpRightFromSquare} className="ms-1" />
                              </Link>
                            </td>
                          </tr>
                          {expanded ? (
                            <tr className="survey-results-detail-row">
                              <td colSpan={7}>
                                {result.status === 'opted_out' ? (
                                  <div className="text-muted">This person opted out and did not answer the survey.</div>
                                ) : result.questionResponses.length === 0 ? (
                                  <div className="text-muted">No answers recorded.</div>
                                ) : (
                                  <dl className="survey-results-answers">
                                    {result.questionResponses.map((answer) => (
                                      <div key={`${result._id}-${answer.questionId}`}>
                                        <dt>{answer.prompt}</dt>
                                        <dd>
                                          {formatAnswer(answer.value, answer.type)}
                                          {answer.additionalInfo ? (
                                            <div className="text-muted small mt-1">{answer.additionalInfo}</div>
                                          ) : null}
                                        </dd>
                                      </div>
                                    ))}
                                  </dl>
                                )}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        {hasFilters ? 'No responses found matching the current filters' : 'No results yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {totalPages > 0 && totalResults > 0 ? (
                <div className="responsive-pagination d-flex justify-content-between align-items-center mt-2">
                  <div className="fw-bold pt-1">
                    {totalResults.toLocaleString()} total response{totalResults === 1 ? '' : 's'}
                  </div>
                  <nav aria-label="Survey response pagination">
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadResults(1)} disabled={currentPage === 1} aria-label="First page">
                          <FontAwesomeIcon icon={faAnglesLeft} />
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadResults(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page">
                          <FontAwesomeIcon icon={faAngleLeft} />
                        </button>
                      </li>
                      <li className="page-item disabled">
                        <span className="page-link">
                          Page {currentPage} of {totalPages}
                        </span>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadResults(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page">
                          <FontAwesomeIcon icon={faAngleRight} />
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => loadResults(totalPages)} disabled={currentPage === totalPages} aria-label="Last page">
                          <FontAwesomeIcon icon={faAnglesRight} />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              ) : null}
            </>
          )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default SurveyResultsPage;
